import * as React from "react";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import type { FormConfig, FormValues, FormStepConfig } from "./types";
import { generateZodSchema, getDefaultValues, VALIDATION_KEYS } from "./types";
import { FormStep } from "./FormStep";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { cn } from "~/lib/utils";
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Shield,
  User,
  Lock,
  Briefcase,
  FileCheck,
  type LucideIcon,
} from "lucide-react";

const iconMap: Record<string, LucideIcon> = {
  user: User,
  lock: Lock,
  briefcase: Briefcase,
  "file-check": FileCheck,
  shield: Shield,
};

interface StepWizardProps {
  config: FormConfig;
  onSubmit: (values: FormValues) => Promise<void>;
  initialValues?: FormValues;
  persistKey?: string;
  persistTimeout?: number;
  className?: string;
  /** Custom icon to render in the current active step indicator (e.g., timer) */
  activeStepIcon?: React.ReactNode;
}

export function StepWizard({
  config,
  onSubmit,
  initialValues,
  persistKey,
  persistTimeout = 30 * 60 * 1000, // 30 minutes default
  className,
  activeStepIcon,
}: StepWizardProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = React.useState(0);
  const [values, setValues] = React.useState<FormValues>(() => ({
    ...getDefaultValues(config),
    ...initialValues,
  }));
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [touched, setTouched] = React.useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  // Screen reader announcement for validation errors
  const [errorAnnouncement, setErrorAnnouncement] = React.useState<string>("");

  const currentStepConfig = config.steps[currentStep];
  const totalSteps = config.steps.length;
  const isLastStep = currentStep === totalSteps - 1;
  const isFirstStep = currentStep === 0;

  // Load persisted state from sessionStorage
  // SECURITY: Using sessionStorage instead of localStorage to ensure data is
  // cleared when the browser tab is closed, reducing data exposure risk
  React.useEffect(() => {
    if (!persistKey || typeof window === "undefined") return;

    try {
      const stored = sessionStorage.getItem(persistKey);
      const timestamp = sessionStorage.getItem(`${persistKey}_timestamp`);

      if (stored && timestamp) {
        const age = Date.now() - parseInt(timestamp, 10);
        if (age < persistTimeout) {
          const parsed = JSON.parse(stored);
          setValues((prev) => ({ ...prev, ...parsed }));
        } else {
          // Clear expired data
          sessionStorage.removeItem(persistKey);
          sessionStorage.removeItem(`${persistKey}_timestamp`);
        }
      }
    } catch {
      // Ignore parse errors
    }
  }, [persistKey, persistTimeout]);

  // Persist state (with security filtering) to sessionStorage
  React.useEffect(() => {
    if (!persistKey || typeof window === "undefined") return;

    // Collect all sensitive field names
    const sensitiveFields = new Set<string>();
    config.steps.forEach((step) => {
      if (step.disablePersistence) {
        step.fields.forEach((field) => sensitiveFields.add(field.name));
      }
      step.fields.forEach((field) => {
        if (field.sensitive || field.type === "password") {
          sensitiveFields.add(field.name);
        }
      });
    });

    // Filter out sensitive data
    const safeValues: FormValues = {};
    for (const [key, value] of Object.entries(values)) {
      if (!sensitiveFields.has(key)) {
        safeValues[key] = value;
      }
    }

    try {
      sessionStorage.setItem(persistKey, JSON.stringify(safeValues));
      sessionStorage.setItem(`${persistKey}_timestamp`, Date.now().toString());
    } catch {
      // Ignore storage errors
    }
  }, [values, persistKey, config.steps]);

  const validateStep = React.useCallback((stepConfig: FormStepConfig): boolean => {
    const schema = generateZodSchema(stepConfig.fields);
    const stepValues: FormValues = {};

    stepConfig.fields.forEach((field) => {
      stepValues[field.name] = values[field.name];
    });

    // Collect additional match field errors
    const matchFieldErrors: Record<string, string> = {};
    stepConfig.fields.forEach((field) => {
      if (field.validation?.matchField) {
        const matchValue = values[field.validation.matchField];
        const currentValue = values[field.name];
        if (currentValue !== matchValue) {
          matchFieldErrors[field.name] = VALIDATION_KEYS.passwordMismatch;
        }
      }
    });

    try {
      schema.parse(stepValues);

      // Check for match field errors even if Zod passes
      if (Object.keys(matchFieldErrors).length > 0) {
        const errorCount = Object.keys(matchFieldErrors).length;
        setErrorAnnouncement(
          t("onboarding.validation.errorsFound", {
            count: errorCount,
            defaultValue: "{{count}} validation error(s) found. Please correct them before continuing."
          })
        );
        setErrors((prevErrors: Record<string, string>) => ({
          ...prevErrors,
          ...matchFieldErrors,
        }));
        setTouched((prevTouched: Record<string, boolean>) => {
          const newTouched = { ...prevTouched };
          stepConfig.fields.forEach((field) => {
            newTouched[field.name] = true;
          });
          return newTouched;
        });
        return false;
      }

      // Clear errors for this step
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        stepConfig.fields.forEach((field) => {
          delete newErrors[field.name];
        });
        return newErrors;
      });
      setErrorAnnouncement(""); // Clear announcement on success
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        const zodError = err as z.ZodError;
        const allErrors: Record<string, string> = { ...matchFieldErrors };
        zodError.issues.forEach((issue) => {
          const path = issue.path.join(".");
          allErrors[path] = issue.message;
        });
        const errorCount = Object.keys(allErrors).length;

        // Announce errors to screen readers
        setErrorAnnouncement(
          t("onboarding.validation.errorsFound", {
            count: errorCount,
            defaultValue: "{{count}} validation error(s) found. Please correct them before continuing."
          })
        );

        setErrors((prevErrors: Record<string, string>) => ({
          ...prevErrors,
          ...allErrors,
        }));
        // Mark all fields as touched
        setTouched((prevTouched: Record<string, boolean>) => {
          const newTouched = { ...prevTouched };
          stepConfig.fields.forEach((field) => {
            newTouched[field.name] = true;
          });
          return newTouched;
        });
      }
      return false;
    }
  }, [values, t]);

  const handleNext = React.useCallback(() => {
    if (validateStep(currentStepConfig)) {
      setCurrentStep((prev: number) => Math.min(prev + 1, totalSteps - 1));
    }
  }, [validateStep, currentStepConfig, totalSteps]);

  const handlePrev = React.useCallback(() => {
    setCurrentStep((prev: number) => Math.max(prev - 1, 0));
  }, []);

  const handleSubmit = React.useCallback(async () => {
    // Validate all steps
    let isValid = true;
    config.steps.forEach((step) => {
      if (!validateStep(step)) {
        isValid = false;
      }
    });

    if (!isValid) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(values);
      // Clear persisted data on successful submission
      if (persistKey) {
        sessionStorage.removeItem(persistKey);
        sessionStorage.removeItem(`${persistKey}_timestamp`);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [config.steps, validateStep, onSubmit, values, persistKey]);

  const handleChange = React.useCallback((name: string, value: unknown) => {
    setValues((prev: FormValues) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    setErrors((prev: Record<string, string>) => {
      if (prev[name]) {
        const next = { ...prev };
        delete next[name];
        return next;
      }
      return prev;
    });
  }, []);

  const handleBlur = React.useCallback((name: string) => {
    setTouched((prev: Record<string, boolean>) => ({ ...prev, [name]: true }));
  }, []);

  const goToStep = React.useCallback((index: number) => {
    // Only allow going to previous steps or current step
    setCurrentStep((prev: number) => (index <= prev ? index : prev));
  }, []);

  /**
   * Convert step ID to camelCase translation key.
   * e.g., "company-verification" → "companyVerification"
   */
  const stepIdToKey = (stepId: string): string => {
    return stepId
      .replace("-info", "")
      .replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
  };

  // Get translated step title and description
  const getStepTitle = (stepId: string) => {
    const key = `onboarding.steps.${stepIdToKey(stepId)}.title`;
    const translated = t(key);
    return translated !== key ? translated : currentStepConfig.title;
  };

  const getStepDescription = (stepId: string) => {
    const key = `onboarding.steps.${stepIdToKey(stepId)}.description`;
    const translated = t(key);
    return translated !== key ? translated : currentStepConfig.description;
  };

  return (
    <div className={cn("w-full max-w-2xl mx-auto", className)}>
      {/* Step Indicator */}
      <div className="mb-6 sm:mb-8 px-4 sm:px-0">
        <div className="flex items-center justify-between mb-4">
          {config.steps.map((step, index) => {
            const Icon = step.icon ? iconMap[step.icon] || Shield : Shield;
            const isCompleted = index < currentStep;
            const isActive = index === currentStep;

            return (
              <React.Fragment key={step.id}>
                <button
                  onClick={() => goToStep(index)}
                  disabled={index > currentStep}
                  className={cn(
                    "step-indicator",
                    // Focus styles for keyboard navigation (WCAG 2.4.7)
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    isActive && "active",
                    isCompleted && "completed",
                    !isActive && !isCompleted && "pending",
                    index <= currentStep && "cursor-pointer",
                    index > currentStep && "cursor-not-allowed opacity-50",
                    // Special styling for active step with custom icon (timer)
                    isActive && activeStepIcon && "p-0 overflow-visible"
                  )}
                  title={getStepTitle(step.id)}
                  aria-label={`${isCompleted ? "Completed: " : isActive ? "Current: " : ""}Step ${index + 1}: ${getStepTitle(step.id)}`}
                  aria-current={isActive ? "step" : undefined}
                >
                  {isCompleted ? (
                    <Check className="h-5 w-5" aria-hidden="true" />
                  ) : isActive && activeStepIcon ? (
                    activeStepIcon
                  ) : (
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
                {index < config.steps.length - 1 && (
                  <div
                    className={cn(
                      "flex-1 h-1 mx-2 rounded-full transition-colors",
                      index < currentStep ? "bg-success" : "bg-muted"
                    )}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Form Card */}
      <Card className="border-2 rounded-none sm:rounded-lg border-x-0 sm:border-x">
        <CardHeader className="space-y-1 pb-4">
          <CardTitle as="h1" className="text-2xl">
            {getStepTitle(currentStepConfig.id)}
          </CardTitle>
          {currentStepConfig.description && (
            <CardDescription>
              {getStepDescription(currentStepConfig.id)}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Screen reader error announcement (WCAG 3.3.1) */}
          <div role="alert" aria-live="assertive" className="sr-only">
            {errorAnnouncement}
          </div>

          <FormStep
            fields={currentStepConfig.fields}
            values={values}
            errors={errors}
            touched={touched}
            onChange={handleChange}
            onBlur={handleBlur}
          />

          {/* Navigation Buttons */}
          <nav className="flex items-center justify-between pt-4 border-t" aria-label={t("common.formNavigation", "Form navigation")}>
            <Button
              type="button"
              variant="outline"
              onClick={handlePrev}
              disabled={isFirstStep || isSubmitting}
              className="gap-2"
              aria-label={t("common.previousStep", "Go to previous step")}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              {t("common.previous")}
            </Button>

            {isLastStep ? (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                isLoading={isSubmitting}
                variant="success"
                className="gap-2"
                aria-label={t("common.submitForm", "Submit the form")}
              >
                <Check className="h-4 w-4" aria-hidden="true" />
                {t("onboarding.submitButton")}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleNext}
                disabled={isSubmitting}
                className="gap-2"
                aria-label={t("common.nextStep", "Go to next step")}
              >
                {t("common.next")}
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            )}
          </nav>
        </CardContent>
      </Card>
    </div>
  );
}
